import { useMemo } from 'react';

import { eventTypeLabels } from './const';
import { useWidgetAnalytics } from './useWidgetAnalytics';
import { Datepicker, Toggle, Table } from '../../components';
import { FormItem, Form, AutoComplete, Container } from '../../components';
import { SubmitButton, Loading, CountryFilterDropdown } from '../../components';
import type { WidgetAnalyticsFormProps } from './useWidgetAnalytics';
import type { FilterValueProps } from '../../components/CountryFilterDropdown';
import type { EventTypeCountStats } from '../../services/widget-analytics';

const WidgetAnalyticsPage = () => {
  const {
    agentsOptions,
    onChange,
    form,
    formId,
    isAgentsLoading,
    billingData,
    sort,
    onSort,
    handleSubmitWidgetAnalytics,
    isSubmitting,
    countries,
  } = useWidgetAnalytics();

  const isDisabled = useMemo(() => {
    return !form?.agentId || !form?.started_at || !form?.ended_at;
  }, [form?.agentId, form?.started_at, form?.ended_at]);

  return (
    <>
      {isAgentsLoading ? (
        <Loading />
      ) : (
        <>
          <Container className="p-2 sm:p-4 md:p-6">
            <div className="grid grid-cols-1 gap-md md:grid-cols-2 xl:grid-cols-2 w-full">
              <Form
                onChange={onChange}
                initialValue={form as WidgetAnalyticsFormProps}
                key={String(formId || 'NEW')}
              >
                <Container className="space-y-md p-0">
                  <FormItem<WidgetAnalyticsFormProps>
                    name="agentId"
                    label="Select Agent"
                    inline={false}
                  >
                    <AutoComplete options={agentsOptions || []} />
                  </FormItem>
                  <div className="flex gap-md">
                    <FormItem<WidgetAnalyticsFormProps>
                      name="distinct"
                      label="Unique Visitors"
                      inline={false}
                    >
                      <Toggle />
                    </FormItem>
                    <div className="flex flex-col justify-end">
                      <FormItem inline={false} name="countries" label="">
                        <CountryFilterDropdown
                          short
                          relatedCountries={countries || []}
                          value={form?.countries as FilterValueProps}
                        />
                      </FormItem>
                    </div>
                  </div>
                </Container>

                <Container className="space-y-md p-0 w-full">
                  <div className="grid grid-cols-1 gap-md">
                    <FormItem<WidgetAnalyticsFormProps>
                      inline={false}
                      name="started_at"
                      label="Start Date"
                    >
                      <Datepicker
                        format="MM/dd/yyyy"
                        maxDate={
                          form?.ended_at ? new Date(form.ended_at) : new Date()
                        }
                      />
                    </FormItem>

                    <FormItem<WidgetAnalyticsFormProps>
                      inline={false}
                      name="ended_at"
                      label="End Date"
                    >
                      <Datepicker
                        endOfDay
                        format="MM/dd/yyyy"
                        cleanOnDisable
                        maxDate={new Date()}
                        minDate={
                          form?.started_at
                            ? new Date(form.started_at)
                            : undefined
                        }
                      />
                    </FormItem>
                  </div>
                </Container>
                <div></div>
                <div className="flex justify-end pt-md">
                  <SubmitButton
                    label="Submit"
                    size="medium"
                    type="primary"
                    onClick={handleSubmitWidgetAnalytics}
                    loading={isSubmitting}
                    disabled={isDisabled}
                  />
                </div>
              </Form>
            </div>
          </Container>

          <div className="mt-md overflow-x-auto px-2 sm:px-4">
            <Table
              verticalAlign="top"
              data={billingData}
              emptyMessage="No Analytics Data"
              sort={sort}
              onSort={onSort}
              config={{
                columns: [
                  {
                    sortName: 'eventType',
                    header: 'Conversation Title',
                    cellClassName:
                      'min-w-[5rem] sm:min-w-[5rem] md:min-w-[6rem]',
                    getValue: ({ eventType: type }: EventTypeCountStats) => (
                      <p className="truncate">
                        {type ? eventTypeLabels[type] || type : ''}
                      </p>
                    ),
                  },
                  {
                    sortName: 'count',
                    header: 'Count',
                    cellClassName:
                      'w-fit min-w-[5rem] sm:min-w-[5rem] md:min-w-[6rem]',
                    getValue: ({ count }) => (
                      <p className="whitespace-nowrap">{count ?? '-'}</p>
                    ),
                  },
                ],
              }}
            />
          </div>
        </>
      )}
    </>
  );
};

export default WidgetAnalyticsPage;
